import React from 'react';
import { Button } from './ui/button';
import { X } from 'lucide-react';

const ETA_OPTIONS = [
  { value: '1m', label: '1 MIN' },
  { value: '3m', label: '3 MIN' },
  { value: '5m', label: '5 MIN' },
  { value: '10m', label: '10 MIN' },
  { value: 'on_site', label: 'ON SITE' },
];

const ETA_PRINTABLE = {
  '1m': '1 min',
  '3m': '3 min',
  '5m': '5 min',
  '10m': '10 min',
  on_site: 'ON SITE',
};

/**
 * Acknowledge / ETA panel rendered inside the GlobalAlertOverlay.
 * Lets responders tap "I'M RESPONDING" with an ETA, see who else has
 * acknowledged, and shows the live "Delivered to N teammates" indicator.
 *
 * Pure presentational component — all state and async logic lives on the
 * parent so this stays trivially testable and reusable.
 */
export const AcknowledgePanel = ({
  alert,
  userId,
  displayColor,
  selectedEta,
  onSelectEta,
  acking,
  onAcknowledge,
  onWithdraw,
}) => {
  const acks = alert?.acknowledgments || [];
  const myAck = acks.find((a) => a.user_id === userId);
  const delivered = alert?.delivered_to_teammates;

  return (
    <div
      className="border-2 rounded-sm mt-2 overflow-hidden bg-black/40"
      style={{ borderColor: displayColor }}
      data-testid="acknowledge-panel"
    >
      <div className="flex items-center justify-between p-2 bg-white/5 border-b border-white/10">
        <span className="font-chivo font-bold text-xs uppercase tracking-wider text-white">
          ⚡ Acknowledge · ETA
        </span>
        <div className="flex items-center gap-3">
          {typeof delivered === 'number' && (
            <span
              className="font-mono text-[10px] uppercase font-bold px-2 py-0.5 rounded-sm bg-white/5 border border-white/10 text-white"
              data-testid="delivery-indicator"
              title="Number of teammates with at least one live device at the moment this alert fired"
            >
              📡 Delivered to {delivered} teammate{delivered === 1 ? '' : 's'}
            </span>
          )}
          <span
            className="font-mono text-[10px] text-muted-foreground"
            data-testid="acknowledgments-count"
          >
            {acks.length} responding
          </span>
        </div>
      </div>

      <div className="p-2 space-y-2">
        {/* ETA selector */}
        <div className="grid grid-cols-5 gap-1">
          {ETA_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onSelectEta(opt.value)}
              data-testid={`eta-${opt.value}`}
              className={`h-8 font-mono text-[10px] uppercase rounded-sm border transition-colors ${
                selectedEta === opt.value
                  ? 'bg-white text-black border-white font-bold'
                  : 'bg-transparent text-white border-white/30 hover:bg-white/10'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Primary action */}
        {myAck ? (
          <div className="flex gap-2 items-center">
            <div
              className="flex-1 h-10 px-3 flex items-center justify-center rounded-sm font-mono text-xs uppercase font-bold text-white"
              style={{ backgroundColor: `${displayColor}40`, border: `1px solid ${displayColor}` }}
              data-testid="my-ack-badge"
            >
              ✓ YOU ACK'D · ETA {ETA_PRINTABLE[myAck.eta] || myAck.eta}
            </div>
            <Button
              onClick={onAcknowledge}
              disabled={acking}
              data-testid="update-eta-btn"
              className="h-10 px-3 font-mono text-[10px] uppercase rounded-sm bg-white/10 hover:bg-white/20 text-white border border-white/30"
            >
              Update
            </Button>
            <Button
              onClick={onWithdraw}
              disabled={acking}
              data-testid="withdraw-ack-btn"
              variant="ghost"
              className="h-10 px-2 font-mono text-[10px] uppercase rounded-sm text-red-400 hover:text-red-300 hover:bg-red-500/10"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <Button
            onClick={onAcknowledge}
            disabled={acking}
            data-testid="acknowledge-btn"
            className="w-full h-11 font-chivo font-black text-sm uppercase tracking-wider rounded-sm text-white"
            style={{
              backgroundColor: displayColor,
              boxShadow: `0 0 16px ${displayColor}80`,
            }}
          >
            {acking ? 'Sending…' : `I'M RESPONDING · ETA ${ETA_PRINTABLE[selectedEta]}`}
          </Button>
        )}

        {/* Roster */}
        {acks.length > 0 && (
          <div className="space-y-1 pt-1" data-testid="acknowledgments-list">
            {acks
              .slice()
              .sort((a, b) => new Date(a.ack_at) - new Date(b.ack_at))
              .map((a) => (
                <div
                  key={a.user_id}
                  className="flex items-center justify-between px-2 py-1 rounded-sm bg-white/5 border border-white/10"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-mono text-[11px] text-green-400">●</span>
                    <span className="font-inter text-xs text-white truncate">{a.name}</span>
                    <span className="font-mono text-[9px] text-muted-foreground uppercase truncate hidden sm:inline">
                      {(a.role || '').replace(/_/g, ' ')}
                    </span>
                  </div>
                  <span
                    className="font-mono text-[10px] uppercase font-bold shrink-0"
                    style={{ color: displayColor }}
                  >
                    {ETA_PRINTABLE[a.eta] || a.eta}
                  </span>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AcknowledgePanel;
